import { RISK_MATRIX_CONSTANTS } from '../risk-assessments-node/constants';
import type { Column, ColumnType } from './types';

// HSE/UK convention: likelihood ascends 1 = Rare -> 5 = Almost certain, so the
// L x S score rises with likelihood. The matrix popover renders label[i] at level
// i+1 (ascending left-to-right) and the score/colour come from the number, not the
// word, so ordering this array low -> high is the whole fix. We hold our own array
// rather than reuse RISK_MATRIX_CONSTANTS.DEFAULT_LIKELIHOOD_LABELS: the standalone
// riskMatrix node and the production Risk Assessments page render their axis top-down
// 5 -> 1, so the descending shared array is correct there and must stay as-is.
const RA_LIKELIHOOD_LABELS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost certain'];

export const RISK_ASSESSMENT_DEFAULTS = {
  LIKELIHOOD_LABELS: [...RA_LIKELIHOOD_LABELS],
  SEVERITY_LABELS: [...RISK_MATRIX_CONSTANTS.DEFAULT_IMPACT_LABELS],
};

// Character caps so an overlong label can't blow up the layout. Axis labels live in
// narrow fixed columns so they're capped tighter than column headers.
export const MAX_COLUMN_NAME_LENGTH = 50;
export const MAX_MATRIX_LABEL_LENGTH = 30;

export const MIN_COLUMN_WIDTH = 60;
export const DEFAULT_COLUMN_WIDTH = 96;
// Structural columns are kept tight so the freed width goes to the editable data
// columns: the action column holds a single 20px trash icon and the row-number column
// a one/two-digit index, so neither needs the full data-column footprint.
export const ACTION_COLUMN_WIDTH = 24;
export const ROW_NUMBER_COLUMN_WIDTH = 28;

// Resize floors, kept tight so the default table fits an A4 portrait page. Rating cells
// wrap to two lines so ~84px shows both; text columns wrap and can go narrower.
export const MIN_TEXT_COLUMN_WIDTH = 56;
export const MIN_RATING_COLUMN_WIDTH = 84;

// Risk-rating columns default to exactly their minimum width: just enough for the
// two-line populated state. A fresh node starts compact (whole table fits the page)
// and the user widens any column from there if they want; resizing one column grows the
// table and scrolls, it does not shrink the others.
export const RATING_COLUMN_WIDTH = MIN_RATING_COLUMN_WIDTH;

// Column type → label shown in the column-type picker. One label per cell
// behaviour: Text (multiline), Date (picker), Risk rating (matrix popover).
export const COLUMN_TYPE_LABELS: Record<ColumnType, string> = {
  text: 'Text',
  date: 'Date',
  riskRating: 'Risk rating',
};

// The matrix must have ≥ 4 rows AND ≥ 4 columns for the four-band Low / Medium /
// High / Very high legend to be visually meaningful. Smaller matrices collapse
// bands together (e.g. a 3x3 has no separate Low + Medium tier).
export const LEGEND_MIN_AXIS = 4;

// Starter columns for a freshly-inserted node (user-editable via the config dialog).
// Widths are tuned so the whole table fits an A4 portrait page without horizontal scroll.
export const DEFAULT_COLUMNS: Column[] = [
  { id: 'hazard', label: 'Hazard', type: 'text', width: 96 },
  { id: 'whoHarmed', label: 'Who might be harmed and how', type: 'text', width: 104 },
  { id: 'initialRiskRating', label: 'Initial risk', type: 'riskRating', width: RATING_COLUMN_WIDTH },
  { id: 'additionalControls', label: 'Control measures', type: 'text', width: 103 },
  { id: 'riskRating', label: 'Residual risk', type: 'riskRating', width: RATING_COLUMN_WIDTH },
  { id: 'owner', label: 'Owner', type: 'text', width: 64 },
];
